import React, { useState, useEffect } from 'react';
import { MessageSquare, Shield, Search, Star, AlertCircle, Lock, Eye, Calendar, Crown, CheckCircle, TrendingUp, Award } from 'lucide-react';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { supabase } from '../../lib/supabase';
import SecureMessageThread from './components/SecureMessageThread';
import ContactDirectory from './components/ContactDirectory';
import PrivacyControls from './components/PrivacyControls';
import EncryptionStatus from './components/EncryptionStatus';
import PremiumFeatures from './components/PremiumFeatures';
import BookingIntegration from './components/BookingIntegration';

const SecureCommunicationCenter = () => {
  const [activeTab, setActiveTab] = useState('messages');
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [_filterType, _setFilterType] = useState('all');
  const [_showPrivacyPanel, _setShowPrivacyPanel] = useState(false);
  const [encryptionLevel, setEncryptionLevel] = useState('high');
  const [messageExpiration, setMessageExpiration] = useState('7days');
  const [screenshotPrevention, setScreenshotPrevention] = useState(true);
  const [anonymousMode, setAnonymousMode] = useState(false);
  const [_onlineUsers, _setOnlineUsers] = useState(new Set());
  const [unreadCounts, setUnreadCounts] = useState({});
  const [userSubscription, setUserSubscription] = useState('free');
  const [_showBookingPanel, _setShowBookingPanel] = useState(false);
  const [reputationScore, setReputationScore] = useState(4.8);
  const [verificationBadges, setVerificationBadges] = useState([]);

  const tabs = [
    { id: 'messages', label: 'Messages Sécurisés', icon: 'MessageSquare', count: conversations?.length || 0 },
    { id: 'contacts', label: 'Annuaire Professionnel', icon: 'Users', count: contacts?.length || 0 },
    { id: 'privacy', label: 'Contrôles Confidentialité', icon: 'Shield' },
    { id: 'booking', label: 'Négociations Booking', icon: 'Calendar' },
    { id: 'premium', label: 'Fonctionnalités Premium', icon: 'Crown' }
  ];

  const subscriptionPlans = {
    'free': {
      name: 'Gratuit',
      features: ['Messagerie basique', 'Profil anonyme limité'],
      messageLimit: 50,
      contactLimit: 25,
      encryptionLevel: 'basic'
    },
    'pro': {
      name: 'Artis.io Pro',
      features: ['Messagerie illimitée', 'Gestion de carrière', 'Outils promotion', 'Analytics profil'],
      messageLimit: -1,
      contactLimit: -1,
      encryptionLevel: 'high'
    },
    'business': {
      name: 'Artis.io Business',
      features: ['Gestion multi-artistes', 'Suivi opportunités', 'Rapports détaillés', 'Support prioritaire'],
      messageLimit: -1,
      contactLimit: -1,
      encryptionLevel: 'enterprise'
    },
    'venue': {
      name: 'Artis.io Venue',
      features: ['Booking talents locaux', 'Interface simplifiée', 'Gestion événements'],
      messageLimit: 100,
      contactLimit: 100,
      encryptionLevel: 'standard'
    }
  };

  useEffect(() => {
    initializeSecureCommunication();
    setupEncryptionHandlers();
    initializeRealtimeUpdates();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- initialisation une seule fois
  }, []);

  useEffect(() => {
    if (selectedConversation) {
      markConversationAsRead();
      setupMessageEncryption();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- dépendances internes stables
  }, [selectedConversation]);

  // Converted to function declarations (hoisted) to avoid TDZ ReferenceError when called in useEffect before definition
  async function initializeSecureCommunication() {
    try {
      setLoading(true);
      await Promise.all([
        getCurrentUser(),
        fetchSecureContacts(),
        fetchEncryptedConversations(),
        loadUserSubscription(),
        initializeReputationSystem()
      ]);
    } catch (error) {
      console.error('Error initializing secure communication:', error);
    } finally {
      setLoading(false);
    }
  }

  async function getCurrentUser() {
    try {
  const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: profile } = await supabase
          .from('user_profiles')
          .select(`
            *,
            partners (
              id,
              partner_type,
              business_name,
              is_verified,
              average_rating,
              specializations
            ),
            user_subscriptions (
              subscription_type,
              status,
              features,
              expires_at
            )
          `)
          .eq('id', user?.id)
          .single();
          
        setCurrentUser(profile);
        setUserSubscription(profile?.user_subscriptions?.[0]?.subscription_type || 'free');
        
        // Load verification badges
        const badges = [];
        if (profile?.partners?.[0]?.is_verified) badges?.push('verified_business');
        if (profile?.partners?.[0]?.average_rating >= 4.5) badges?.push('top_rated');
        setVerificationBadges(badges);
      }
    } catch (error) {
      console.error('Error getting current user:', error);
    }
  }

  async function fetchSecureContacts() {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select(`
          *,
          partners (
            id,
            partner_type,
            business_name,
            is_verified,
            average_rating,
            specializations,
            location
          ),
          user_subscriptions (
            subscription_type,
            status
          )
        `)
  .neq('id', (await supabase.auth.getUser())?.data?.user?.id)
  .eq('partners.is_verified', true);

      if (error) throw error;

      // Add privacy protection - only show verified professionals
      const secureContacts = data?.map(contact => ({
        ...contact,
        // Generate anonymous identifier instead of real name initially
        display_name: anonymousMode ? `Professionnel ${contact?.id?.slice(-4)}` : contact?.full_name,
        // Hide sensitive data until connection approved
        protected_email: contact?.email ? '***@***.***' : null,
        protected_phone: contact?.phone ? '+33 ** ** ** **' : null,
        connection_status: 'none', // none, requested, connected
        reputation_score: Math.random() * 5, // Mock reputation system
        last_activity: new Date(Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000),
        industry_specialization: contact?.partners?.[0]?.specializations || [],
        subscription_tier: contact?.user_subscriptions?.[0]?.subscription_type || 'free'
      })) || [];

      setContacts(secureContacts);
    } catch (error) {
      console.error('Error fetching secure contacts:', error);
    }
  }

  async function fetchEncryptedConversations() {
    try {
  const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('secure_conversations')
        .select(`
          *,
          participants:conversation_participants (
            user_id,
            encryption_key,
            user_profiles (
              id,
              full_name,
              avatar_url,
              partners (
                partner_type,
                business_name,
                is_verified
              )
            )
          ),
          last_message:messages (
            content,
            created_at,
            message_type,
            is_encrypted
          )
        `)
  .eq('conversation_participants.user_id', user?.id)
  .order('updated_at', { ascending: false });

      if (error) throw error;

      const processedConversations = data?.map(conv => ({
        ...conv,
        unread_count: Math.floor(Math.random() * 5), // Mock unread count
        encryption_status: 'active',
        security_level: encryptionLevel,
        participants: conv?.participants?.filter(p => p?.user_id !== user?.id)
      })) || [];

      setConversations(processedConversations);

      // Calculate unread counts
      const unreadMap = {};
      processedConversations?.forEach(conv => {
        unreadMap[conv?.id] = conv?.unread_count;
      });
      setUnreadCounts(unreadMap);

    } catch (error) {
      console.error('Error fetching encrypted conversations:', error);
    }
  }

  async function loadUserSubscription() {
    try {
  const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data } = await supabase
        .from('user_subscriptions')
        .select('*')
        .eq('user_id', user?.id)
        .eq('status', 'active')
        .single();

      if (data) {
        setUserSubscription(data?.subscription_type);
      }
    } catch (error) {
      console.error('Error loading user subscription:', error);
    }
  }

  async function initializeReputationSystem() {
    // Mock reputation system based on successful interactions
    const mockScore = 4.2 + Math.random() * 0.8;
    setReputationScore(mockScore);
  }

  function setupEncryptionHandlers() {
    // Initialize end-to-end encryption
    if (screenshotPrevention) {
      // Disable screenshot capability (mock implementation)
      document.addEventListener('keyup', (e) => {
        if (e?.key === 'PrintScreen') {
          e?.preventDefault();
          alert('Capture d\'écran désactivée pour la sécurité');
        }
      });
    }
  }

  function setupMessageEncryption() {
    // Mock encryption setup for selected conversation
    // debug encryption hook (suppressed in production)
    if (selectedConversation && encryptionLevel === 'high') {
      /* noop instrumentation */
    }
  }

  function initializeRealtimeUpdates() {
    // Setup realtime subscriptions for secure messaging
    const conversationSubscription = supabase
      .channel('secure_conversations')
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'secure_conversations' },
        () => {
          fetchEncryptedConversations();
        }
      )
      .subscribe();

    return () => {
      conversationSubscription?.unsubscribe();
    };
  }

  async function markConversationAsRead() {
    if (!selectedConversation || !currentUser) return;

    try {
      await supabase
        ?.from('conversation_read_status')
        ?.upsert({
          conversation_id: selectedConversation?.id,
          user_id: currentUser?.id,
          last_read_at: new Date()?.toISOString()
        });

      // Update local unread count
      setUnreadCounts(prev => ({ ...prev, [selectedConversation?.id]: 0 }));
    } catch (error) {
      console.error('Error marking conversation as read:', error);
    }
  }

  async function initiateSecureConnection(contact) {
    if (!currentUser) return;

    try {
      // Create encrypted connection request
      const { data: _request, error } = await supabase
        .from('connection_requests')
        .insert({
          requester_id: currentUser?.id,
          recipient_id: contact?.id,
          message: `Demande de connexion sécurisée de ${currentUser?.full_name}`,
          request_type: 'secure_connection',
          privacy_level: encryptionLevel
        })
  .select()
  .single();

      if (error) throw error;

      alert('Demande de connexion sécurisée envoyée!');
      
    } catch (error) {
      console.error('Error initiating secure connection:', error);
    }
  }

  function startBookingNegotiation(contact) {
    setSelectedConversation({
      id: `booking_${contact?.id}`,
      type: 'booking_negotiation',
      participants: [{ user_profiles: contact }],
      booking_context: {
        event_type: null,
        date_range: null,
        budget_range: null,
        venue_details: null
      }
    });
  _setShowBookingPanel(true);
    setActiveTab('booking');
  }

  function getSubscriptionBadge(subscriptionType) {
    const colors = {
      'free': 'bg-gray-100 text-gray-800',
      'pro': 'bg-blue-100 text-blue-800',
      'business': 'bg-purple-100 text-purple-800',
      'venue': 'bg-green-100 text-green-800'
    };

    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors?.[subscriptionType] || colors?.free}`}>
        {subscriptionPlans?.[subscriptionType]?.name || 'Gratuit'}
      </span>
    );
  }

  function getCurrentTime() {
    return new Intl.DateTimeFormat('fr-FR', {
      weekday: 'long',
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    })?.format(new Date());
  }

  function getEncryptionIcon(level) {
    switch (level) {
      case 'high': return <Shield className="w-4 h-4 text-green-500" />;
      case 'enterprise': return <Lock className="w-4 h-4 text-blue-500" />;
      case 'standard': return <Eye className="w-4 h-4 text-yellow-500" />;
      default: return <AlertCircle className="w-4 h-4 text-gray-500" />;
    }
  }

  function renderTabContent() {
    switch (activeTab) {
      case 'messages':
        return (
          <div className="flex h-full">
            {/* Conversations List */}
            <div className="w-1/3 border-r border-gray-200 dark:border-neutral-800 bg-white dark:bg-neutral-900/70 backdrop-blur supports-[backdrop-filter]:bg-white/70 dark:supports-[backdrop-filter]:bg-neutral-900/60">
              <div className="p-4 border-b border-gray-200 dark:border-neutral-800">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400 dark:text-neutral-500" />
                    <input
                      type="text"
                      placeholder="Rechercher des conversations..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e?.target?.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-200 dark:border-neutral-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-neutral-900 text-gray-900 dark:text-neutral-100 placeholder:text-gray-400 dark:placeholder:text-neutral-500"
                    />
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => _setShowPrivacyPanel(true)}
                  >
                    <Shield className="w-4 h-4" />
                  </Button>
                </div>
                
                <EncryptionStatus 
                  level={encryptionLevel}
                  screenshotPrevention={screenshotPrevention}
                  anonymousMode={anonymousMode}
                />
              </div>

              <div className="divide-y divide-gray-100 dark:divide-neutral-800">
                {conversations?.filter(conv => 
                  conv?.participants?.[0]?.user_profiles?.full_name?.toLowerCase()?.includes(searchQuery?.toLowerCase())
                )?.map((conversation) => (
                  <div
                    key={conversation?.id}
                    onClick={() => setSelectedConversation(conversation)}
                    className={`p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-neutral-800/70 ${selectedConversation?.id === conversation?.id ? 'bg-blue-50 dark:bg-blue-900/30 border-r-2 border-blue-500/80' : ''}`}
                  >
                    <div className="flex items-center space-x-3">
                      <div className="relative">
                        <img
                          src={conversation?.participants?.[0]?.user_profiles?.avatar_url || '/api/placeholder/40/40'}
                          alt=""
                          className="w-10 h-10 rounded-full"
                        />
                        {getEncryptionIcon(conversation?.security_level)}
                        <div className="absolute -top-1 -right-1">
                          {getEncryptionIcon(conversation?.security_level)}
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <p className="font-medium text-gray-900 dark:text-neutral-100 truncate">
                            {anonymousMode 
                              ? `Professionnel ${conversation?.id?.slice(-4)}` 
                              : conversation?.participants?.[0]?.user_profiles?.full_name
                            }
                          </p>
                          <span className="text-xs text-gray-500 dark:text-neutral-500">14:30</span>
                        </div>
                        <p className="text-sm text-gray-500 dark:text-neutral-400 truncate">
                          {conversation?.last_message?.content || 'Nouvelle conversation sécurisée'}
                        </p>
                        <div className="flex items-center justify-between mt-1">
                          <div className="flex items-center space-x-2">
                            <span className="text-xs bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300 px-2 py-1 rounded">
                              Chiffré E2E
                            </span>
                            {conversation?.participants?.[0]?.user_profiles?.partners?.[0]?.is_verified && (
                              <CheckCircle className="w-4 h-4 text-blue-500" />
                            )}
                          </div>
                          {unreadCounts?.[conversation?.id] > 0 && (
                            <span className="bg-blue-500 text-white text-xs rounded-full px-2 py-1 min-w-[1.5rem] text-center">
                              {unreadCounts?.[conversation?.id]}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {/* Message Thread */}
            <div className="flex-1">
              {selectedConversation ? (
                <SecureMessageThread
                  conversation={selectedConversation}
                  currentUser={currentUser}
                  encryptionLevel={encryptionLevel}
                  messageExpiration={messageExpiration}
                  onStartBooking={() => _setShowBookingPanel(true)}
                />
              ) : (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <MessageSquare className="w-16 h-16 text-gray-300 dark:text-neutral-700 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-neutral-100 mb-2">Centre de Communication Sécurisé</h3>
                    <p className="text-gray-500 dark:text-neutral-400 max-w-sm">
                      Communiquez en toute sécurité avec des professionnels vérifiés sans révéler vos coordonnées personnelles
                    </p>
                    <Button
                      className="mt-4"
                      onClick={() => setActiveTab('contacts')}
                    >
                      Parcourir l&apos;Annuaire
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        );

      case 'contacts':
        return (
          <ContactDirectory
            contacts={contacts}
            currentUser={currentUser}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            onInitiateConnection={initiateSecureConnection}
            onStartBooking={startBookingNegotiation}
            anonymousMode={anonymousMode}
            userSubscription={userSubscription}
            subscriptionPlans={subscriptionPlans}
          />
        );

      case 'privacy':
        return (
          <PrivacyControls
            encryptionLevel={encryptionLevel}
            setEncryptionLevel={setEncryptionLevel}
            messageExpiration={messageExpiration}
            setMessageExpiration={setMessageExpiration}
            screenshotPrevention={screenshotPrevention}
            setScreenshotPrevention={setScreenshotPrevention}
            anonymousMode={anonymousMode}
            setAnonymousMode={setAnonymousMode}
            currentUser={currentUser}
            userSubscription={userSubscription}
          />
        );

      case 'booking':
        return (
          <BookingIntegration
            selectedConversation={selectedConversation}
            currentUser={currentUser}
            userSubscription={userSubscription}
            onCreateContract={() => {/* TODO: create secure contract */}}
          />
        );

      case 'premium':
        return (
          <PremiumFeatures
            currentSubscription={userSubscription}
            subscriptionPlans={subscriptionPlans}
            reputationScore={reputationScore}
            verificationBadges={verificationBadges}
            onUpgrade={(_plan) => {/* TODO: upgrade plan */}}
          />
        );

      default:
        return null;
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-neutral-950">
        <Header />
        <div className="pt-16">
          <div className="flex items-center justify-center min-h-[calc(100vh-4rem)]">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
              <p className="text-gray-600">Initialisation sécurisée en cours...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-neutral-950 text-gray-900 dark:text-neutral-100">
      <Header />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
              <div>
                <div className="flex items-center space-x-3 mb-2">
                  <div className="flex items-center justify-center w-12 h-12 bg-blue-500 rounded-lg">
                    <Shield className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h1 className="text-3xl font-bold text-gray-900">
                      Centre de Communication Sécurisé
                    </h1>
                    <p className="text-gray-600">
                      {getCurrentTime()} • Communication anonyme et chiffrée
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="mt-4 lg:mt-0 flex items-center space-x-3">
                <div className="flex items-center space-x-2 bg-white dark:bg-neutral-900 px-4 py-2 rounded-lg border border-gray-200 dark:border-neutral-700 text-gray-800 dark:text-neutral-200">
                  <Star className="w-4 h-4 text-yellow-500" />
                  <span className="text-sm font-medium">Score: {reputationScore?.toFixed(1)}</span>
                </div>
                {getSubscriptionBadge(userSubscription)}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setActiveTab('premium')}
                >
                  <Crown className="w-4 h-4 mr-2" />
                  Upgrade
                </Button>
              </div>
            </div>

            {/* Security Status Banner */}
            <div className="mt-6 bg-gradient-to-r from-green-50 to-blue-50 dark:from-green-900/20 dark:to-blue-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="flex items-center justify-center w-10 h-10 bg-green-500 rounded-full">
                    <Shield className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-neutral-100">Sécurité Maximale Active</h3>
                    <p className="text-sm text-gray-600 dark:text-neutral-400">
                      Chiffrement E2E • Anonymat préservé • Coordonnées protégées
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span className="text-sm font-medium text-green-700 dark:text-green-400">Sécurisé</span>
                </div>
              </div>
            </div>
          </div>

          {/* Navigation Tabs */}
          <div className="mb-8">
            <div className="border-b border-gray-200 dark:border-neutral-800 overflow-x-auto">
              <nav className="flex space-x-8 min-w-max">
                {tabs?.map((tab) => (
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                      activeTab === tab?.id
                        ? 'border-blue-500 text-blue-600 dark:border-blue-400 dark:text-blue-400' :'border-transparent text-gray-500 dark:text-neutral-500 hover:text-gray-700 dark:hover:text-neutral-300 hover:border-gray-300 dark:hover:border-neutral-600'
                    }`}
                  >
                    <Icon name={tab?.icon} size={16} />
                    <span>{tab?.label}</span>
                    {tab?.count !== undefined && (
                      <span className={`ml-2 px-2 py-1 rounded-full text-xs ${
                        activeTab === tab?.id 
                          ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-300' :'bg-gray-100 text-gray-600 dark:bg-neutral-800 dark:text-neutral-400'
                      }`}>
                        {tab?.count}
                      </span>
                    )}
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Tab Content */}
          <div className="bg-white dark:bg-neutral-900/70 rounded-lg border border-gray-200 dark:border-neutral-800" style={{ minHeight: '600px' }}>
            {renderTabContent()}
          </div>

          {/* Economic Model Integration Footer */}
          <div className="mt-12 bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 dark:from-blue-900/20 dark:via-purple-900/20 dark:to-pink-900/20 rounded-lg p-8 border border-blue-100 dark:border-blue-900/40">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-neutral-100 mb-4">
                Écosystème Complet Artis.io
              </h2>
              <p className="text-gray-600 dark:text-neutral-400 max-w-3xl mx-auto">
                Communication sécurisée, booking intelligent, formation professionnelle et système d&apos;affiliation intégrés
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mx-auto mb-4">
                  <MessageSquare className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-neutral-100 mb-2">Communication Anonyme</h3>
                <p className="text-sm text-gray-600 dark:text-neutral-400">
                  Négociez et collaborez sans révéler vos coordonnées personnelles
                </p>
              </div>
              
              <div className="text-center">
                <div className="flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mx-auto mb-4">
                  <Calendar className="w-8 h-8 text-purple-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-neutral-100 mb-2">Booking Automatisé</h3>
                <p className="text-sm text-gray-600 dark:text-neutral-400">
                  Solutions de booking et gestion de talents avec commission automatique
                </p>
              </div>
              
              <div className="text-center">
                <div className="flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mx-auto mb-4">
                  <Award className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-neutral-100 mb-2">Artis.io Academy</h3>
                <p className="text-sm text-gray-600 dark:text-neutral-400">
                  Formation professionnelle continue et certifications reconnues
                </p>
              </div>
              
              <div className="text-center">
                <div className="flex items-center justify-center w-16 h-16 bg-orange-100 rounded-full mx-auto mb-4">
                  <TrendingUp className="w-8 h-8 text-orange-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-neutral-100 mb-2">Système d&apos;Affiliation</h3>
                <p className="text-sm text-gray-600 dark:text-neutral-400">
                  Commissions sur bookings réussis et parrainage premium
                </p>
              </div>
            </div>

            <div className="mt-8 pt-8 border-t border-gray-200 dark:border-neutral-800">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center space-x-4 mb-4 sm:mb-0">
                  <Button
                    variant="default"
                    onClick={() => setActiveTab('premium')}
                  >
                    <Crown className="w-4 h-4 mr-2" />
                    Découvrir Premium
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {/* TODO: open academy */}}
                  >
                    <Award className="w-4 h-4 mr-2" />
                    Artis.io Academy
                  </Button>
                </div>
                <div className="text-sm text-gray-500 dark:text-neutral-500">
                  Plateforme sécurisée • Données chiffrées • Confiance vérifiée
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default SecureCommunicationCenter;